import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, fromEvent } from 'rxjs';
import { GlobalSearchContext } from '../models/global-search.model';
import { openGlobalSearch } from '../functions/open-global-search.function';

/**
 * Binds Ctrl+K / Cmd+K to the global search palette. Call `initialize()` once from
 * the app shell; scope resolution is pluggable via the optional factory so the
 * palette always opens with the currently active object/context.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchHotkeyService {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------
    private readonly injector = inject(Injector);

    private readonly document = inject(DOCUMENT);

    private readonly destroyRef = inject(DestroyRef);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    private _isOpen = false;

    private _contextFactory: (() => GlobalSearchContext) | undefined;

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Starts listening for the shortcut. Idempotent per app lifetime — call from the shell's constructor.
     *
     * @param contextFactory - Resolves the dialog context at open time (e.g. reads the active object from a store).
     * @returns {void}
     */
    initialize(contextFactory?: () => GlobalSearchContext): void {
        this._contextFactory = contextFactory;

        fromEvent<KeyboardEvent>(this.document, 'keydown')
            .pipe(
                filter((event) => (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k'),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                event.preventDefault();

                this.open();
            });
    }

    /**
     * Opens the palette programmatically (also used by the shortcut). No-ops while one is already open.
     *
     * @returns {void}
     */
    open(): void {
        if (this._isOpen) {
            return;
        }

        this._isOpen = true;

        openGlobalSearch(this.injector, this._contextFactory?.() ?? {})
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this._isOpen = false;
            });
    }
}
